import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Ticket } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eyebrow, DisplayHeading } from "@/components/editorial";
import { redeemFreeCode, type RedeemResult } from "@/lib/free-codes.functions";

export const Route = createFileRoute("/redeem")({
  head: () => ({
    meta: [
      { title: "Redeem a free review code — Tiger's Eye" },
      {
        name: "description",
        content: "Enter your Tiger's Eye code to claim your free review.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (
    <AuthGate>
      <RedeemPage />
    </AuthGate>
  ),
});

const ERROR_MESSAGES: Record<string, string> = {
  not_signed_in: "Please sign in first.",
  invalid_code: "That code isn't recognised — check the spelling and try again.",
  code_inactive: "That code has been switched off.",
  already_redeemed: "You've already used that code.",
  code_exhausted: "That code has been fully used.",
};

export default function RedeemPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  const redeemMutation = useMutation({
    mutationFn: (value: string) => redeemFreeCode({ data: { code: value } }),
    onSuccess: (result: RedeemResult) => {
      if (result.ok) {
        const until = (result as { free_until?: string }).free_until;
        const pretty = until ? until.split("-").reverse().join("/") : null;
        toast.success(pretty ? `Code accepted — you're free until ${pretty}.` : "Code accepted — your free term is ready.");
        navigate({ to: "/reviews/new" });
      } else {
        toast.error(ERROR_MESSAGES[result.error] ?? "That code didn't work.");
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <main className="mx-auto max-w-md px-6 py-16">
      <Eyebrow className="mb-3">Free review</Eyebrow>
      <DisplayHeading level={1} className="mb-4">Redeem your code</DisplayHeading>
      <p className="mb-8 text-sm text-ink-secondary">
        Got a code from a YouTuber, your university or a friend? Enter it below
        to claim your free review.
      </p>

      <form
        className="flex flex-col gap-4 rounded-xl border border-rule bg-surface p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) redeemMutation.mutate(code.trim());
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="redeem-code">Your code</Label>
          <Input
            id="redeem-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="TEYE-XXXXXX"
            className="font-mono uppercase"
            autoComplete="off"
            required
          />
        </div>
        <Button type="submit" disabled={redeemMutation.isPending || !code.trim()}>
          <Ticket className="mr-1.5 h-4 w-4" />
          {redeemMutation.isPending ? "Checking…" : "Redeem code"}
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-ink-tertiary">
        No code? <Link to="/pricing" className="underline">See pricing</Link> — the
        first 49 students join free.
      </p>
    </main>
  );
}
